import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useUserRole } from '@/hooks/useUserRole'
import { showApiError, showValidationMessage } from '@/lib/utils/notifications'
import { fetchStatusList } from '@/features/resident/services/residentApi'
import {
  constantsToSelectOptions,
  EMPTY_RESIDENT_LIST_LABEL_MAPS,
  parseStatusListLabelMaps,
} from '@/features/resident/utils/residentListConstants'
import type { ResidentListLabelMaps } from '@/features/resident/types'
import { getReimbursements } from '../services/reimbursementsService'
import {
  DEFAULT_PER_PAGE,
  DEFAULT_STATUSES,
  EMPTY_PAGINATION,
  EMPTY_TOTALS,
  LIBERTY_ROLES,
  REIMBURSEMENT_TYPES,
  RENTAL_STATUSES,
} from '../constants'
import type {
  ReimbursementFilters,
  ReimbursementListItem,
  ReimbursementTotals,
  ReimbursementsPaginationState,
  UseReimbursementsListReturn,
} from '../types'
import {
  saveReimbursementFiltersToSession,
  readReimbursementFiltersFromSession,
  clearReimbursementFiltersFromSession,
} from '../utils/sessionFilterStorage'
import { readReimbursementsListHandoffFromLocation } from '../utils/reimbursementsListHandoffSearch'

const LEGACY_LIST_ORDER_BY: Record<string, string> = {
  rental_status: 'reimbursement_rental_status',
  rental_progress: 'reimbursement_rental_progress',
}

const LEGACY_TYPE_CODES = new Set(['SKIPPED', 'EVICTION'])

function migrateListFiltersFromSession(
  saved: Partial<ReimbursementFilters>,
): Partial<ReimbursementFilters> {
  let next = { ...saved }

  if (next.orderBy) {
    const migratedOrderBy = LEGACY_LIST_ORDER_BY[next.orderBy]
    if (migratedOrderBy) {
      next = { ...next, orderBy: migratedOrderBy }
    }
  }

  const rentalStatus = next.rentalStatus
  if (Array.isArray(rentalStatus) && rentalStatus.length > 0) {
    const typeCodes = rentalStatus.filter((code) =>
      LEGACY_TYPE_CODES.has(String(code).trim().toUpperCase()),
    )
    if (typeCodes.length > 0) {
      const existingType = next.reimbursement_type ?? []
      next = {
        ...next,
        reimbursement_type: [...new Set([...existingType, ...typeCodes])],
        rentalStatus: rentalStatus.filter(
          (code) => !LEGACY_TYPE_CODES.has(String(code).trim().toUpperCase()),
        ),
      }
    }
  }

  return next
}

export function useReimbursementsList(): UseReimbursementsListReturn {
  const { roleName, orgId, orgName, companyId, companyName, propertyId, propertyName } = useUserRole()

  const isLibertyRole = useMemo(
    () => (roleName ? LIBERTY_ROLES.includes(roleName as (typeof LIBERTY_ROLES)[number]) : false),
    [roleName],
  )
  const isExternalInsurance = roleName === 'EXTERNAL_INSURANCE'

  const defaultFilters = useMemo<ReimbursementFilters>(() => {
    const base: ReimbursementFilters = {
      page: 1,
      perPage: DEFAULT_PER_PAGE,
      // Match Angular default list payload ordering.
      orderBy: 'resident_name',
      sortBy: 'asc',
      // Angular defaults to Liberty Rent (1) for liberty roles
      org_id: isLibertyRole ? '1' : (orgId ?? ''),
      org_name: isLibertyRole ? (orgName || 'Liberty Rent') : (orgName ?? undefined),
      companyId: companyId ?? '',
      company_name: companyName ?? undefined,
      propertyId: propertyId ?? '',
      property_name: propertyName ?? undefined,
      past_customer: '1',
      claim_status: isExternalInsurance ? ['EXTERNAL_REVIEW'] : [],
    }
    return base
  }, [orgId, orgName, companyId, companyName, propertyId, propertyName, isLibertyRole, isExternalInsurance])

  const [filters, setFilters] = useState<ReimbursementFilters>(() => {
    const saved = readReimbursementFiltersFromSession()
    return saved
      ? ({ ...defaultFilters, ...migrateListFiltersFromSession(saved) } as ReimbursementFilters)
      : defaultFilters
  })
  const filtersRef = useRef(filters)
  filtersRef.current = filters

  const [items, setItems] = useState<ReimbursementListItem[]>([])
  const [totals, setTotals] = useState<ReimbursementTotals>(EMPTY_TOTALS)
  const [pagination, setPagination] = useState<ReimbursementsPaginationState>(EMPTY_PAGINATION)
  const [loading, setLoading] = useState(false)
  const [statusLabelMaps, setStatusLabelMaps] = useState<ResidentListLabelMaps>(
    EMPTY_RESIDENT_LIST_LABEL_MAPS,
  )

  useEffect(() => {
    void fetchStatusList()
      .then((statusList) => setStatusLabelMaps(parseStatusListLabelMaps(statusList)))
      .catch(() => setStatusLabelMaps(EMPTY_RESIDENT_LIST_LABEL_MAPS))
  }, [])

  const reimbursementTypeOptions = useMemo(() => {
    if (isExternalInsurance) return []
    const options = constantsToSelectOptions(statusLabelMaps.reimbursement_type)
    return options.length ? options : constantsToSelectOptions(REIMBURSEMENT_TYPES)
  }, [isExternalInsurance, statusLabelMaps.reimbursement_type])

  const rentalStatusOptions = useMemo(() => {
    if (isExternalInsurance) return []
    const options = constantsToSelectOptions(statusLabelMaps.reimbursement_rental_status)
    return options.length ? options : constantsToSelectOptions(RENTAL_STATUSES)
  }, [isExternalInsurance, statusLabelMaps.reimbursement_rental_status])

  const defaultStatusOptions = useMemo(() => {
    if (isExternalInsurance) return []
    const options = constantsToSelectOptions(statusLabelMaps.reimbursement_rental_progress)
    return options.length ? options : constantsToSelectOptions(DEFAULT_STATUSES)
  }, [isExternalInsurance, statusLabelMaps.reimbursement_rental_progress])

  // Guards against a stale fetch's `finally` clobbering a newer fetch's loading state.
  const fetchSeqRef = useRef(0)

  const fetch = useCallback(async (next: ReimbursementFilters) => {
    const seq = ++fetchSeqRef.current
    setLoading(true)
    try {
      const effectiveOrgId =
        next.org_id !== '' && next.org_id !== null && next.org_id !== undefined
          ? next.org_id
          : orgId ?? (isLibertyRole ? 1 : undefined)
      const resp = await getReimbursements(next, effectiveOrgId)
      // Ignore stale responses — application handoff applies `q` after an
      // unfiltered initial request can already be in flight (noopener new tab).
      if (seq !== fetchSeqRef.current) return

      setItems(resp.data ?? [])

      // Angular parity: render the single response's `total_count` aggregate verbatim,
      // no extra per-status requests.
      setTotals(resp.total_count ?? EMPTY_TOTALS)

      setPagination({
        current: resp.current ?? 1,
        last: resp.last ?? 1,
        total: resp.total ?? 0,
      })
    } catch (e) {
      if (seq !== fetchSeqRef.current) return
      const message =
        e && typeof e === 'object' && 'message' in e ? String((e as { message?: unknown }).message ?? '') : ''
      // Angular default-list: notify(error.error, 0) → Validation Message; interceptor sends user to dashboard.
      if (message === 'Access Denied') {
        showValidationMessage('Access Denied', 'Validation Message')
      } else {
        showApiError(e)
      }
      setItems([])
      setTotals(EMPTY_TOTALS)
      setPagination(EMPTY_PAGINATION)
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false)
    }
  }, [isLibertyRole, orgId])

  // Initial load
  const initialLoadRef = useRef(false)
  useEffect(() => {
    if (initialLoadRef.current) return
    if (!roleName) return

    const seededFilters: ReimbursementFilters = { ...filtersRef.current }
    let changed = false

    // Angular routeParams / application Default button: `?applicant_id=` → `q`.
    // URL is required because `window.open(..., 'noopener')` does not share sessionStorage.
    const handoff = readReimbursementsListHandoffFromLocation()
    if (handoff.q && seededFilters.q !== handoff.q) {
      seededFilters.q = handoff.q
      changed = true
    }
    if (handoff.past_customer && seededFilters.past_customer !== handoff.past_customer) {
      seededFilters.past_customer = handoff.past_customer
      changed = true
    }

    if (
      (seededFilters.org_id === '' || seededFilters.org_id === null || seededFilters.org_id === undefined) &&
      defaultFilters.org_id !== '' &&
      defaultFilters.org_id !== null &&
      defaultFilters.org_id !== undefined
    ) {
      seededFilters.org_id = defaultFilters.org_id
      changed = true
    }

    if (
      (seededFilters.companyId === '' ||
        seededFilters.companyId === null ||
        seededFilters.companyId === undefined) &&
      defaultFilters.companyId !== '' &&
      defaultFilters.companyId !== null &&
      defaultFilters.companyId !== undefined
    ) {
      seededFilters.companyId = defaultFilters.companyId
      changed = true
    }

    if (
      (seededFilters.propertyId === '' ||
        seededFilters.propertyId === null ||
        seededFilters.propertyId === undefined) &&
      defaultFilters.propertyId !== '' &&
      defaultFilters.propertyId !== null &&
      defaultFilters.propertyId !== undefined
    ) {
      seededFilters.propertyId = defaultFilters.propertyId
      changed = true
    }

    if (
      isExternalInsurance &&
      (!Array.isArray(seededFilters.claim_status) || seededFilters.claim_status.length === 0)
    ) {
      seededFilters.claim_status = ['EXTERNAL_REVIEW']
      changed = true
    }

    if (changed) {
      setFilters(seededFilters)
      filtersRef.current = seededFilters
      saveReimbursementFiltersToSession(seededFilters)
    }

    initialLoadRef.current = true
    void fetch(seededFilters)
  }, [defaultFilters, fetch, isExternalInsurance, roleName])

  const applyFilters = useCallback(
    (overrides: Partial<ReimbursementFilters>) => {
      const next: ReimbursementFilters = {
        ...filtersRef.current,
        ...overrides,
        // Reset to page 1 unless caller is explicitly paging
        page: overrides.page ?? 1,
      }
      setFilters(next)
      saveReimbursementFiltersToSession(next)
      void fetch(next)
    },
    [fetch],
  )

  const resetFilters = useCallback(() => {
    clearReimbursementFiltersFromSession()
    setFilters(defaultFilters)
    void fetch(defaultFilters)
  }, [defaultFilters, fetch])

  const changePage = useCallback(
    (page: number) => applyFilters({ page }),
    [applyFilters],
  )

  const changePerPage = useCallback(
    (perPage: number) => applyFilters({ perPage, page: 1 }),
    [applyFilters],
  )

  const sort = useCallback(
    (orderBy: string) => {
      const current = filtersRef.current
      const sortBy: 'asc' | 'desc' = current.sortBy === 'asc' ? 'desc' : 'asc'
      applyFilters({ orderBy, sortBy, page: 1 })
    },
    [applyFilters],
  )

  const refresh = useCallback(() => {
    void fetch(filtersRef.current)
  }, [fetch])

  return {
    items,
    totals,
    pagination,
    loading,
    filters,
    applyFilters,
    resetFilters,
    changePage,
    changePerPage,
    sort,
    refresh,
    statusLabelMaps,
    reimbursementTypeOptions,
    rentalStatusOptions,
    defaultStatusOptions,
  }
}
